// ProteoRisk on the common XOmics webserver lifecycle
// (${WEBSERVER_API_ORIGIN}/api/proteorisk), through the shared client exactly
// as the other webservers use it.
import {
  createServiceUpload,
  defaultServicePipeline,
  loadServiceCatalogue,
  loadServiceJobs,
  openJobDownload,
  serviceRequest,
  webserverJobStatus,
} from '../../shared/xomics-webserver-client';

export const SERVICE_CODE = 'proteorisk';

// The report and the one-row summary workbook, by the file patterns the
// olink-proteomics 2.0.0 pipeline declares for them (literal "${JOB}").
// eslint-disable-next-line no-template-curly-in-string
export const REPORT_PATTERN = '${JOB}_olink_report.json';
// eslint-disable-next-line no-template-curly-in-string
export const SUMMARY_PATTERN = 'new_schema/${JOB}_olink_report_summary.xlsx';

const TERMINAL = new Set(['completed', 'failed', 'cancelled']);

export function isTerminal(status) {
  return TERMINAL.has(status);
}

// The catalogue decides the pipeline, its sample type and its workbook slot.
export async function loadProteoRiskContract(token) {
  const catalogue = await loadServiceCatalogue(SERVICE_CODE, token);
  const pipeline = defaultServicePipeline(catalogue);
  const inputs = Array.isArray(pipeline.inputs) ? pipeline.inputs : [];
  const workbookInputs = inputs.filter((input) => String(input.file_format).toLowerCase() === 'xlsx');
  if (workbookInputs.length !== 1) {
    throw new Error('The active ProteoRisk pipeline does not declare exactly one workbook input.');
  }
  const sampleCodes = Array.isArray(pipeline.sample_codes) ? pipeline.sample_codes : [];
  if (!sampleCodes.length) {
    throw new Error('The active ProteoRisk pipeline has no sample type yet, so new analyses cannot be started.');
  }
  return {
    pipeline,
    workbookSlot: Number(workbookInputs[0].position),
    sampleCodes,
  };
}

export async function submitAnalysis({
  token, contract, sampleCode, file, displayName, age, sex, bmi, onProgress,
}) {
  return createServiceUpload({
    serviceCode: SERVICE_CODE,
    token,
    pipeline: contract.pipeline,
    sampleCode,
    selectedFiles: [{ file, slotPosition: contract.workbookSlot, label: 'Olink Explore HT workbook' }],
    // XOmics refuses an external upload without an integer age and a sex.
    patientParams: { age, sex },
    // BMI is optional: absent means missing to the model, never a placeholder.
    pipelineParams: bmi === null ? null : { BMI: bmi },
    displayName,
    metadata: {
      age,
      sex,
      bmi,
      original_filename: file.name,
    },
    onProgress,
  });
}

export async function loadJobs(token) {
  const { jobs } = await loadServiceJobs(SERVICE_CODE, token, { limit: 50 });
  return jobs;
}

export async function loadUpload(token, uploadUuid) {
  return serviceRequest(SERVICE_CODE, token, `/uploads/${encodeURIComponent(uploadUuid)}`);
}

// One upload has one ProteoRisk job; its status uses the shared mapping.
export function uploadStatus(detail) {
  const job = Array.isArray(detail?.jobs) && detail.jobs.length ? detail.jobs[0] : null;
  return { job, status: webserverJobStatus(job, detail?.upload) };
}

function outputPath(uploadUuid, jobUuid) {
  return `/uploads/${encodeURIComponent(uploadUuid)}/jobs/${encodeURIComponent(jobUuid)}/outputs`;
}

export async function listOutputs(token, uploadUuid, jobUuid) {
  const body = await serviceRequest(SERVICE_CODE, token, outputPath(uploadUuid, jobUuid));
  return Array.isArray(body.outputs) ? body.outputs : [];
}

// The output whose declared pattern is `pattern`. An output without a declared
// pattern is matched by the same pattern applied to its file name.
export function findOutput(outputs, pattern) {
  // eslint-disable-next-line no-template-curly-in-string
  const suffix = pattern.replace('${JOB}', '');
  const baseSuffix = suffix.split('/').pop();
  const matches = outputs.filter((output) => (output.file_pattern
    ? output.file_pattern === pattern
    : String(output.filename || '').endsWith(baseSuffix)));
  return matches.length === 1 ? matches[0] : null;
}

async function signedOutput(token, uploadUuid, jobUuid, outputId) {
  const body = await serviceRequest(
    SERVICE_CODE,
    token,
    `${outputPath(uploadUuid, jobUuid)}/${encodeURIComponent(outputId)}/download`,
  );
  if (!body.downloadUrl) throw new Error('The file is not available for download yet.');
  return body;
}

async function fetchSigned(signed, kind) {
  const response = await fetch(signed.downloadUrl);
  if (!response.ok) throw new Error(`The ${kind} could not be downloaded (${response.status}).`);
  return response;
}

export async function loadReportFile(token, uploadUuid, jobUuid, outputs) {
  const output = findOutput(outputs, REPORT_PATTERN);
  if (!output) throw new Error('The report file is missing from this analysis.');
  const signed = await signedOutput(token, uploadUuid, jobUuid, output.id);
  const response = await fetchSigned(signed, 'report');
  return response.json();
}

// Profile spread and the share of proteins beyond 3 SD live only in the
// pipeline's one-row summary workbook, not in the report JSON.
export async function loadSummaryRow(token, uploadUuid, jobUuid, outputs) {
  const output = findOutput(outputs, SUMMARY_PATTERN);
  if (!output) throw new Error('The summary workbook is missing from this analysis.');
  const signed = await signedOutput(token, uploadUuid, jobUuid, output.id);
  const response = await fetchSigned(signed, 'summary workbook');
  const buffer = await response.arrayBuffer();
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheet = workbook.Sheets.olink_report_summary;
  if (!sheet) throw new Error('The summary workbook has no olink_report_summary sheet.');
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: null });
  if (rows.length !== 1) throw new Error('The summary workbook does not have exactly one row.');
  const spread = Number(rows[0].residual_spread);
  const beyond = Number(rows[0].residual_beyond_3sd_fraction);
  if (!Number.isFinite(spread) || !Number.isFinite(beyond)) {
    throw new Error('The summary workbook has no profile spread.');
  }
  return { residualSpread: spread, beyondThreeSdFraction: beyond };
}

export async function openOutputDownload(token, uploadUuid, jobUuid, outputs, pattern) {
  const output = findOutput(outputs, pattern);
  if (!output) throw new Error('This file is missing from the analysis.');
  const signed = await signedOutput(token, uploadUuid, jobUuid, output.id);
  window.open(signed.downloadUrl, '_blank', 'noopener,noreferrer');
}

export function openBundleDownload(token, uploadUuid, jobUuid) {
  return openJobDownload(SERVICE_CODE, token, { upload_uuid: uploadUuid, job_uuid: jobUuid });
}

// The job's own frozen parameters: exactly the age, sex and BMI it ran with.
export function jobParameters(job) {
  if (!job || job.params === null || job.params === undefined) return null;
  let params = job.params;
  if (typeof params === 'string') {
    try {
      params = JSON.parse(params);
    } catch (_error) {
      return null;
    }
  }
  if (!params || typeof params !== 'object') return null;
  const bmi = params.BMI === undefined || params.BMI === null || params.BMI === '' ? null : Number(params.BMI);
  return {
    age: params.AGE === undefined ? null : Number(params.AGE),
    sex: params.SEX || null,
    bmi: Number.isFinite(bmi) ? bmi : null,
  };
}
